import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * A coluna da direita do editor, com largura que a pessoa escolhe.
 *
 * Era fixa em 380px. Cabia a lista de gatilhos, mas não o que realmente
 * acontece ali: o prompt da IA, o corpo de um template da Meta, a mensagem
 * com botões e a prévia do WhatsApp — tudo espremido numa coluna em que
 * qualquer texto de duas linhas vira seis.
 *
 * Mesmo gesto do painel do Assistente (`AssistenteDock`): arrastar a borda,
 * largura guardada no navegador. Aqui ela é compartilhada pelos quatro
 * painéis (configuração, pendências, prateleira, gatilho) — ajustar de novo
 * a cada troca de painel seria a mesma tarefa repetida quatro vezes.
 *
 * O conteúdo é um `@container`: quem está dentro reage à largura do painel,
 * não à da janela. É o que deixa o editor de mensagem virar duas colunas
 * quando há espaço, sem ninguém precisar passar a medida adiante.
 */

const CHAVE_LARGURA = 'fluxo:largura-painel';
const LARGURA_PADRAO = 440;
const LARGURA_MIN = 340;
const LARGURA_LARGA = 720;
/** Teto relativo: em tela pequena, um painel de 900px não deixa canvas nenhum. */
const FRACAO_MAXIMA = 0.6;

function larguraGuardada(): number {
  try {
    const v = Number(localStorage.getItem(CHAVE_LARGURA));
    return Number.isFinite(v) && v >= LARGURA_MIN ? v : LARGURA_PADRAO;
  } catch {
    return LARGURA_PADRAO;
  }
}

export function PainelLateral({ children }: { children: React.ReactNode }) {
  const [largura, setLargura] = useState(larguraGuardada);
  const [arrastando, setArrastando] = useState(false);

  const limitar = useCallback(
    (n: number) => Math.min(Math.max(n, LARGURA_MIN), Math.max(LARGURA_MIN, window.innerWidth * FRACAO_MAXIMA)),
    [],
  );

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_LARGURA, String(largura));
    } catch {
      // Navegador sem storage (aba anônima com restrição): a largura vale só
      // para esta sessão, o que é melhor que quebrar o painel.
    }
  }, [largura]);

  // A janela pode encolher depois (dividir a tela, girar o monitor) e deixar
  // o painel maior que o teto relativo.
  useEffect(() => {
    const aoRedimensionar = () => setLargura((l) => limitar(l));
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, [limitar]);

  useEffect(() => {
    if (!arrastando) return;
    const mover = (e: MouseEvent) => setLargura(limitar(window.innerWidth - e.clientX));
    const soltar = () => setArrastando(false);
    document.addEventListener('mousemove', mover);
    document.addEventListener('mouseup', soltar);
    // Sem isto o arrasto seleciona o texto do painel inteiro.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', mover);
      document.removeEventListener('mouseup', soltar);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [arrastando, limitar]);

  const larguraLarga = limitar(LARGURA_LARGA);
  const estaLargo = largura >= larguraLarga - 1;
  const alternar = () => setLargura(estaLargo ? LARGURA_PADRAO : larguraLarga);

  return (
    <div
      className="relative shrink-0 border-l bg-background flex flex-col overflow-hidden"
      style={{ width: largura }}
    >
      <div
        onMouseDown={() => setArrastando(true)}
        onDoubleClick={() => setLargura(LARGURA_PADRAO)}
        title="Arraste para redimensionar · dois cliques volta ao tamanho padrão"
        className={`absolute left-0 top-0 z-10 h-full w-1.5 cursor-col-resize transition-colors ${
          arrastando ? 'bg-primary/40' : 'hover:bg-primary/20'
        }`}
        aria-hidden
      />

      <button
        type="button"
        onClick={alternar}
        title={estaLargo ? 'Estreitar o painel' : 'Alargar o painel'}
        aria-label={estaLargo ? 'Estreitar o painel' : 'Alargar o painel'}
        className="absolute left-0 top-1/2 z-20 flex h-12 w-4 -translate-x-1/2 -translate-y-1/2 items-center
                   justify-center rounded-full border bg-background text-muted-foreground shadow-sm
                   transition-colors hover:text-foreground hover:border-primary/40"
      >
        {estaLargo ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>

      <div className="@container flex-1 flex flex-col overflow-hidden">{children}</div>
    </div>
  );
}
